import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { APP_NAME } from '@/shared/lib/brand';
import { useContentLocale } from '@/shared/lib/locale';
import { browser, privacyPageUrl, urlOf } from '@/shared/lib/site';
import { Footer, type FooterLink } from '@/shared/ui/shell';

/** The front page is a static page per language, beside the application (frontend/site). */
export const frontPageUrl = (locale: string) => urlOf('home', `/${locale}/`);

/**
 * The foot of every page of the application: the logo and a link, which lead to the
 * front page, and `actions` of the application's own.
 */
export function AppFooter({ actions }: { actions?: ReactNode }) {
  const { t } = useTranslation();
  const locale = useContentLocale();
  const front = frontPageUrl(locale);
  // The help and the glossary are sections of every interface that has them: the foot
  // does not say them twice.
  const links: FooterLink[] = [
    { id: 'home', label: t('footer.frontPage'), href: front },
    { id: 'privacy', label: t('footer.privacy'), href: privacyPageUrl(locale) },
  ];
  // The static pages are no pages of the application, even at the same address.
  const go = (id: string) => browser.leave(links.find((link) => link.id === id)?.href ?? front);
  return (
    <Footer
      brand={APP_NAME}
      brandHref={front}
      brandLabel={t('nav.frontPage', { app: APP_NAME })}
      links={links}
      onNavigate={go}
      navLabel={t('footer.label')}
      actions={actions}
      note={t('footer.rights', { year: new Date().getFullYear(), app: APP_NAME })}
    />
  );
}
